import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/api/client'

export interface MyTenantRow {
  id: number
  name: string
  slug: string
  subdomain: string | null
  status: string
  is_owner: boolean
  is_admin: boolean
  /** True once the ERP mirror users exist for the workspace. */
  erp_mirror_ready?: boolean
  /**
   * `ready` (mirror users exist), `in_progress` (provisioning job queued / running) or
   * `needs_credentials` (never enqueued, failed or canceled — the owner must supply the
   * ERP admin password). Only the last one blocks the portal.
   */
  erp_provisioning_state?: 'ready' | 'in_progress' | 'needs_credentials'
  plan_code: string | null
  plan_name: string | null
  subscription_status: string | null
  unpaid_invoice_count: number
  unpaid_amount_total: number
  first_unpaid_invoice_id: number | null
}

/**
 * Cached list from ``GET /me/tenants`` for routing and the tenants index page.
 */
export const useMyTenantsStore = defineStore('myTenants', () => {
  const tenants = ref<MyTenantRow[]>([])
  const loaded = ref(false)
  const loading = ref(false)

  async function fetchMyTenants(): Promise<void> {
    loading.value = true
    try {
      const { data } = await api.get<{ tenants: MyTenantRow[] }>('/me/tenants')
      tenants.value = data.tenants || []
      loaded.value = true
    } catch {
      // Transient failure: preserve any previously loaded list and keep
      // loaded=false so the navigation guard retries on the next route
      // change instead of treating an empty cache as "no memberships" and
      // redirecting a legitimate user to /forbidden until a full reload.
    } finally {
      loading.value = false
    }
  }

  function isAdminForTenant(tenantId: number): boolean {
    return tenants.value.some((t) => t.id === tenantId && t.is_admin)
  }

  function memberRow(tenantId: number): MyTenantRow | undefined {
    return tenants.value.find((t) => t.id === tenantId)
  }

  function reset(): void {
    tenants.value = []
    loaded.value = false
  }

  return {
    tenants,
    loaded,
    loading,
    fetchMyTenants,
    isAdminForTenant,
    memberRow,
    reset,
  }
})
